import type {StdSignDoc, AminoSignResponse} from '@cosmjs/amino';
import type {OfflineSigner} from '@cosmjs/proto-signing';
import type {SigningStargateClient} from '@cosmjs/stargate';
import type {ManagerContext} from '@cosmos-kit/core';
import type {CosmosChainInfo} from 'chains/cosmos/cosmos.types';

type CosmosChainId = string;

export type WalletStatus = 'connected' | 'disconnected' | 'connecting';

export enum WalletProviderEnum {
  WEB3_AUTH = 'web3_auth',
  COSMOS_KIT = 'cosmos_kit',
}

export type WalletProvider = `${WalletProviderEnum}`;

export type ExplorerUrlType = keyof CosmosChainInfo['explorerURL'];

export interface WalletProviderInterface<O = unknown> {
  type: WalletProvider;

  walletType: CosmosKitWalletType;

  loginProvider: Web3AuthLoginProvider;

  connected: boolean;

  elysAddress: string;

  cosmosAddress: string;

  elysChain: CosmosChainInfo;

  cosmosChain: CosmosChainInfo;

  offlineSigner: OfflineSigner;

  accountChangeEvent?: string;

  installURL?: string;

  init: () => Promise<void>;

  connect(sessionCheck?: boolean, options?: O): Promise<void>;

  disconnect(): Promise<void>;

  refreshAccount(): Promise<void>;

  resolveAddress(chainId: CosmosChainId): Promise<string>;

  getOfflineSigner(chainId: CosmosChainId): Promise<OfflineSigner>;

  getOfflineSignerAmino(chainId: CosmosChainId): Promise<{
    signAmino: (
      signerAddress: string,
      signDoc: StdSignDoc,
    ) => Promise<AminoSignResponse>;
  }>;

  createSigningStargateClient: (
    chainId: CosmosChainId,
    offlineSigner?: OfflineSigner,
  ) => Promise<SigningStargateClient>;
}

export type Web3AuthLoginProvider =
  | 'email_passwordless'
  | 'google'
  | 'discord'
  | 'reddit'
  | 'apple'
  | 'twitter';

export type Web3AuthProviderOptions = {
  loginProvider: Web3AuthLoginProvider;
  username?: string;
};

export type DirectAuthProviderOptions = {
  privateKey: string;
};

export type CosmosKitWalletType =
  | 'keplr-extension'
  | 'leap-extension'
  | 'keplr-mobile'
  | 'leap-cosmos-mobile'
  | 'cosmostation-mobile'
  | 'trust-mobile'
  | 'cosmostation-extension'
  | 'cosmos-extension-metamask'
  | 'leap-metamask-cosmos-snap'
  | 'okxwallet-extension'
  | 'station-extension'
  | 'xdefi-extension'
  | 'ledger-web-usb-hid';

export type CosmosKitAuthProviderOptions = {
  manager: ManagerContext;
  walletType: CosmosKitWalletType;
};

export class ExtensionNotInstalledError extends Error {
  url = '';

  constructor(url: string) {
    super('The extension is not installed.');
    this.url = url;
  }
}
